import React, { useEffect, useMemo, useRef, useState } from "react";
import {
	createNotification,
	CustomButton,
	CustomDialog,
	icon,
	InfiniteList2,
} from "../../../../../../common/components";
import { useApi } from "../../../../../../common/hooks";
import { environmentAllowed, translate } from "../../../../../../common/providers";
import { AiAgentsService } from "../../../../../../api";
import { downloadFile } from "../../../../../../common/utils";
import FilterHeader from "../FilterHeader";
import TabHeaders from "../TabHeaders";
import { AI_MODEL_TABS } from "../tabs";
import { mapAgentModelError, MODEL_IMPORT_ERROR, parseModelsImportPayload, resolveExportFilename } from "../../utils";
import AiModelRow from "./AiModelRow";
import ModelFormDialog from "./ModelFormDialog";
import styles from "./AiModelsManager.module.css";

const AI_MODEL_COLUMN_WIDTHS_STORAGE_KEY = "dsi-ai-model-column-widths";
const DEFAULT_VISIBLE_COLUMNS = AI_MODEL_TABS.map(({ key }) => key);
const MIN_COLUMN_WIDTH = 120;
const ACTIONS_WIDTH = 96;
const ACTIONS_WIDTH_WITH_EXPORT = 140;

const filterModels = (models, normalizedSearch) => {
	if (!normalizedSearch) {
		return models;
	}
	return models.filter((model) =>
		[
			model.name,
			model.description,
			model.deploymentName,
			model.deployment,
			model.modelName,
			model.region,
			model.projectId,
			model.location,
		]
			.filter(Boolean)
			.some((value) => value.toLowerCase().includes(normalizedSearch))
	);
};

const sortModels = (models, sortKey, sortDirection) =>
	[...models].sort((firstModel, secondModel) => {
		const firstValue = `${firstModel[sortKey] || ""}`.toLowerCase();
		const secondValue = `${secondModel[sortKey] || ""}`.toLowerCase();
		if (firstValue === secondValue) {
			return 0;
		}
		return sortDirection === "asc" ? firstValue.localeCompare(secondValue) : secondValue.localeCompare(firstValue);
	});

export default function AiModelsManager() {
	const [count, setCount] = useState(0);
	const [search, setSearch] = useState("");
	const [sortDirection, setSortDirection] = useState("asc");
	const [sortKey, setSortKey] = useState("name");
	const [allModels, setAllModels] = useState([]);
	const [selectedModel, setSelectedModel] = useState(null);
	const [openDialog, setOpenDialog] = useState(false);
	const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
	const [infiniteListRequest, setInfiniteListRequest] = useState(null);
	const [columnWidths, setColumnWidths] = useState(() => {
		try {
			const storedWidths = window.localStorage.getItem(AI_MODEL_COLUMN_WIDTHS_STORAGE_KEY);
			const parsed = storedWidths ? JSON.parse(storedWidths) : {};
			return Object.fromEntries(
				Object.entries(parsed)
					.map(([key, value]) => [key, Number(value)])
					.filter(([, value]) => Number.isFinite(value) && value > 0)
			);
		} catch (error) {
			return {};
		}
	});
	const [pendingImport, setPendingImport] = useState(null);
	const resizeStateRef = useRef(null);
	const containerRef = useRef(null);
	const fileInputRef = useRef(null);
	const { call: getModelList } = useApi(AiAgentsService.getModelList);
	const { call: createModel } = useApi(AiAgentsService.createModel);
	const { call: updateModel } = useApi(AiAgentsService.updateModel);
	const { call: deleteModel } = useApi(AiAgentsService.deleteModel);
	const { call: exportAllModels, isLoading: isExportingAll } = useApi(AiAgentsService.exportAllModels);
	const { call: importModels, isLoading: isImporting } = useApi(AiAgentsService.importModels);
	const isDev = environmentAllowed(["dev"]);
	const actionsWidth = isDev ? ACTIONS_WIDTH_WITH_EXPORT : ACTIONS_WIDTH;
	const visibleColumns = DEFAULT_VISIBLE_COLUMNS;
	const refresh = () => {
		setCount((prev) => prev + 1);
	};
	useEffect(() => {
		let cancelled = false;
		getModelList({ page: 0, size: 200, sort: "name;asc" })
			.then((response) => {
				if (!cancelled) {
					setAllModels(Array.isArray(response?.contents) ? response.contents : []);
				}
			})
			.catch((error) => {
				if (!cancelled) {
					createNotification({
						type: "error",
						message: mapAgentModelError(error) || translate("canvas.error-network"),
					});
				}
			});
		return () => {
			cancelled = true;
		};
	}, [count, getModelList]);
	const displayedModels = useMemo(() => {
		const normalizedSearch = search.trim().toLowerCase();
		return sortModels(filterModels(allModels, normalizedSearch), sortKey, sortDirection);
	}, [allModels, search, sortKey, sortDirection]);
	useEffect(() => {
		setInfiniteListRequest(() => async () => ({
			contents: displayedModels,
			hasMore: false,
			totalPages: 1,
			totalElements: displayedModels.length,
		}));
	}, [displayedModels]);
	useEffect(() => {
		const handleMouseMove = (event) => {
			if (!resizeStateRef.current) {
				return;
			}
			const { columnKey, startWidth, startX } = resizeStateRef.current;
			const nextWidth = Math.max(MIN_COLUMN_WIDTH, startWidth - (startX - event.clientX));
			setColumnWidths((prev) => ({ ...prev, [columnKey]: nextWidth }));
		};
		const handleMouseUp = () => {
			if (!resizeStateRef.current) {
				return;
			}
			resizeStateRef.current = null;
			setColumnWidths((prev) => {
				window.localStorage.setItem(AI_MODEL_COLUMN_WIDTHS_STORAGE_KEY, JSON.stringify(prev));
				return prev;
			});
		};
		window.addEventListener("mousemove", handleMouseMove);
		window.addEventListener("mouseup", handleMouseUp);
		return () => {
			window.removeEventListener("mousemove", handleMouseMove);
			window.removeEventListener("mouseup", handleMouseUp);
		};
	}, []);
	useEffect(() => {
		const element = containerRef.current;
		if (!element) {
			return;
		}
		setColumnWidths((prev) => {
			const currentTotalWidth = visibleColumns.reduce((total, columnKey) => total + (prev[columnKey] || 0), 0);
			if (currentTotalWidth > 0) {
				return prev;
			}
			const availableWidth = Math.max(
				element.clientWidth - actionsWidth,
				visibleColumns.length * MIN_COLUMN_WIDTH
			);
			const evenWidth = Math.max(MIN_COLUMN_WIDTH, Math.floor(availableWidth / visibleColumns.length));
			return visibleColumns.reduce((accumulator, columnKey) => ({ ...accumulator, [columnKey]: evenWidth }), {});
		});
	}, [actionsWidth, visibleColumns]);
	const handleOpenCreate = () => {
		setSelectedModel(null);
		setOpenDialog(true);
	};
	const handleSubmit = (payload) => {
		const request = selectedModel ? updateModel({ modelId: selectedModel.id }, payload) : createModel(payload);
		Promise.resolve(request)
			.then(() => {
				createNotification({ type: "success", message: translate("dsi.agents.ai-models.success.saved") });
				setOpenDialog(false);
				refresh();
			})
			.catch((error) => {
				createNotification({
					type: "error",
					message: mapAgentModelError(error) || translate("canvas.error-network"),
				});
			});
	};
	const handleDelete = () => {
		const modelId = selectedModel?.id;
		if (!modelId) {
			return;
		}
		deleteModel({ modelId })
			.then(() => {
				createNotification({ type: "success", message: translate("dsi.agents.ai-models.success.deleted") });
				setOpenDeleteDialog(false);
				setSelectedModel(null);
				refresh();
			})
			.catch((error) => {
				createNotification({
					type: "error",
					message: mapAgentModelError(error) || translate("canvas.error-network"),
				});
			});
	};
	const handleDeletePrompt = (item) => {
		setSelectedModel(item);
		setOpenDeleteDialog(true);
	};
	const handleEditPrompt = (item) => {
		setSelectedModel(item);
		setOpenDialog(true);
	};
	const handleExportAll = () => {
		exportAllModels()
			.then(({ data, filename }) => {
				downloadFile({
					data,
					filename: resolveExportFilename(filename, "models_export.json"),
					filetype: "json",
				});
			})
			.catch((error) => {
				createNotification({
					type: "error",
					message: mapAgentModelError(error) || translate("dsi.agents.ai-models.export.failure"),
				});
			});
	};
	const handleSelectImportFile = (event) => {
		const file = event.target.files?.[0];
		event.target.value = "";
		if (!file) {
			return;
		}
		file.text()
			.then((text) => {
				const models = parseModelsImportPayload(text);
				setPendingImport({ models, fileName: file.name });
			})
			.catch((error) => {
				const messageKey =
					error?.code === MODEL_IMPORT_ERROR.NOT_ARRAY
						? "dsi.agents.ai-models.import.error.not-array"
						: "dsi.agents.ai-models.import.error.invalid-json";
				createNotification({ type: "error", message: translate(messageKey) });
			});
	};
	const handleConfirmImport = () => {
		if (!pendingImport || isImporting) {
			return;
		}
		importModels(pendingImport.models)
			.then(() => {
				createNotification({ type: "success", message: translate("dsi.agents.ai-models.import.success") });
				setPendingImport(null);
				refresh();
			})
			.catch((error) => {
				createNotification({
					type: "error",
					message: mapAgentModelError(error) || translate("dsi.agents.ai-models.import.failure"),
				});
			});
	};
	const handleResizeStart = (event, columnKey) => {
		event.preventDefault();
		resizeStateRef.current = {
			columnKey,
			startWidth: Number(columnWidths[columnKey]) || MIN_COLUMN_WIDTH,
			startX: event.clientX,
		};
	};
	const handleClickSort = (key) => {
		if (!key) {
			return;
		}
		if (key === sortKey) {
			setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
		} else {
			setSortKey(key);
			setSortDirection("asc");
		}
	};
	const rowRenderer = (model) => (
		<AiModelRow
			key={model.id}
			actionsWidth={actionsWidth}
			columnWidths={columnWidths}
			isDev={isDev}
			minColumnWidth={MIN_COLUMN_WIDTH}
			model={model}
			visibleColumns={visibleColumns}
			onDelete={handleDeletePrompt}
			onEdit={handleEditPrompt}
		/>
	);
	return (
		<div className={styles.container}>
			<FilterHeader onChangeFilters={({ name }) => setSearch(name)} />
			<div className={styles.toolbar}>
				<CustomButton color="primary" startIcon={icon.faPlus} variant="contained" onClick={handleOpenCreate}>
					{translate("dsi.agents.ai-models.new.button")}
				</CustomButton>
				{isDev && (
					<>
						<input
							ref={fileInputRef}
							hidden
							accept=".json,application/json"
							type="file"
							onChange={handleSelectImportFile}
						/>
						<CustomButton
							color="secondary"
							disabled={isImporting}
							startIcon={icon.faFileImport}
							variant="outlined"
							onClick={() => fileInputRef.current?.click()}
						>
							{translate("dsi.agents.ai-models.import.button")}
						</CustomButton>
						<CustomButton
							color="secondary"
							disabled={isExportingAll}
							startIcon={icon.faFileExport}
							variant="outlined"
							onClick={handleExportAll}
						>
							{translate("dsi.agents.ai-models.export-all.button")}
						</CustomButton>
					</>
				)}
			</div>
			<div ref={containerRef} className={styles.table}>
				<TabHeaders
					columnWidths={columnWidths}
					sortDirection={sortDirection}
					sortKey={sortKey}
					tabs={AI_MODEL_TABS}
					visibleTabKeys={visibleColumns}
					onClickSort={handleClickSort}
					onResizeStart={handleResizeStart}
				/>
				<div className={styles.list}>
					<InfiniteList2 request={infiniteListRequest} rowRenderer={rowRenderer} />
				</div>
			</div>
			<ModelFormDialog
				model={selectedModel}
				open={openDialog}
				onClose={() => setOpenDialog(false)}
				onSubmit={handleSubmit}
			/>
			<CustomDialog
				iconColor="var(--color-red)"
				iconModel={icon.faExclamationTriangle}
				open={openDeleteDialog}
				subTitle={translate("dsi.agents.ai-models.delete.warning")}
				title={translate("common:delete-confirmation-popup.title")}
				onClose={() => setOpenDeleteDialog(false)}
				onSubmit={handleDelete}
			/>
			<CustomDialog
				iconColor="var(--color-orange)"
				iconModel={icon.faExclamationTriangle}
				open={!!pendingImport}
				submitDisabled={isImporting}
				submitLabel={translate("common:btn.validate")}
				subTitle={translate("dsi.agents.ai-models.import.confirm.message", {
					count: pendingImport?.models?.length ?? 0,
				})}
				title={translate("dsi.agents.ai-models.import.confirm.title")}
				onClose={() => setPendingImport(null)}
				onSubmit={handleConfirmImport}
			/>
		</div>
	);
}
